import * as Popover from "@radix-ui/react-popover";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { Activity, Library, MessageCircle, Plus, Sparkles, UserRound } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { api, ApiError, setSignedOutHandler } from "@/lib/api";
import { connectEvents } from "@/lib/events";
import { cn } from "@/lib/format";
import { AssetViewer } from "./AssetViewer";
import { GpuStatus } from "./GpuStatus";
import { Spinner } from "./ui";

const NAV = [
  { to: "/create", label: "Create", icon: Sparkles },
  { to: "/chat", label: "Chat", icon: MessageCircle },
  { to: "/library", label: "Library", icon: Library },
  { to: "/activity", label: "Activity", icon: Activity },
] as const;

/**
 * Desktop: a quiet sidebar with the chat list. Phone: a top bar with the GPU status and a tab bar at the
 * thumb, both clear of the Dynamic Island and the home indicator. The sign-in page gets no shell.
 */
export function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ strict: false }) as { asset?: string };
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: 60_000 });
  const queryClient = useQueryClient();

  // The live stream is personal, so it only opens once we know who is signed in.
  useEffect(() => {
    if (!me.data) return;
    return connectEvents(queryClient);
  }, [me.data, queryClient]);

  // Any 401 (an expired session, a studio that was reset) sends the person to the sign-in page.
  useEffect(() => {
    setSignedOutHandler(() => {
      if (window.location.pathname !== "/login") navigate({ to: "/login" });
    });
  }, [navigate]);

  const onLogin = pathname === "/login";
  useEffect(() => {
    if (!onLogin && me.isError && (me.error as ApiError)?.status === 401) navigate({ to: "/login" });
  }, [onLogin, me.isError, me.error, navigate]);

  if (onLogin) return <Outlet />;
  if (me.isLoading) {
    return (
      <div className="flex h-dvh-screen items-center justify-center bg-desk">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-dvh-screen overflow-hidden bg-desk">
      <Sidebar pathname={pathname} />
      <div className="flex min-w-0 flex-1 flex-col">
        <MobileTopBar pathname={pathname} />
        <main className="relative min-h-0 flex-1">
          <Outlet />
        </main>
        <TabBar pathname={pathname} />
      </div>
      <AssetViewer
        assetId={search.asset ?? null}
        onClose={() => navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, asset: undefined }) })}
      />
    </div>
  );
}

function Wordmark() {
  return (
    <Link to="/create" search={{ kind: "video" }} className="font-display text-[19px] tracking-[-0.01em] text-paper">
      studio
    </Link>
  );
}

function navSearch(to: string) {
  if (to === "/create") return { kind: "video" as const };
  if (to === "/library") return { filter: "all" as const };
  return undefined;
}

/** Name, account, admin and the way out. */
function AccountMenu({ className }: { className?: string }) {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: 60_000 });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = me.data?.user;
  const item = "flex h-10 items-center rounded-[10px] px-3 text-sm text-paper outline-none hover:bg-raised";
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-full px-2.5 text-[13px] font-medium text-mute transition-colors hover:bg-panel hover:text-paper",
            className,
          )}
        >
          <UserRound className="size-[18px]" aria-hidden />
          <span className="max-w-[120px] truncate md:inline">{user?.displayName ?? "Account"}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[220px] rounded-[var(--radius-control)] border border-line bg-panel p-1 shadow-2xl shadow-black/40 outline-none"
        >
          {user ? <p className="px-3 py-2 text-[13px] text-mute">Signed in as {user.username}</p> : null}
          <Popover.Close asChild>
            <Link to="/account" className={item}>
              Settings
            </Link>
          </Popover.Close>
          {user?.role === "admin" ? (
            <Popover.Close asChild>
              <Link to="/admin" className={item}>
                People, skills and servers
              </Link>
            </Popover.Close>
          ) : null}
          <Popover.Close asChild>
            <button
              type="button"
              className={cn(item, "w-full text-left")}
              onClick={async () => {
                await api.logout().catch(() => {});
                queryClient.clear();
                navigate({ to: "/login" });
              }}
            >
              Sign out
            </button>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Sidebar({ pathname }: { pathname: string }) {
  const chats = useQuery({ queryKey: ["chats"], queryFn: api.chats });
  const navigate = useNavigate();
  return (
    <aside className="hidden w-[264px] shrink-0 flex-col border-r border-line/60 bg-desk md:flex">
      <div className="flex h-16 items-center justify-between px-5">
        <Wordmark />
      </div>
      <nav className="flex flex-col gap-0.5 px-3" aria-label="Main">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              search={navSearch(to)}
              className={cn(
                "flex h-10 items-center gap-3 rounded-[10px] px-3 text-sm font-medium transition-colors",
                active ? "bg-panel text-paper" : "text-mute hover:bg-panel/60 hover:text-paper",
              )}
            >
              <Icon className="size-[18px]" aria-hidden />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="mt-6 flex items-center justify-between px-5">
        <span className="text-[13px] font-medium text-mute">Chats</span>
        <button
          type="button"
          onClick={async () => {
            const thread = await api.createChat();
            await chats.refetch();
            navigate({ to: "/chat/$chatId", params: { chatId: thread.id } });
          }}
          className="inline-flex size-8 items-center justify-center rounded-full text-mute hover:bg-panel hover:text-paper"
          aria-label="New chat"
          title="New chat"
        >
          <Plus className="size-4" />
        </button>
      </div>
      <div className="scrollbar-thin mt-1 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {chats.data?.length ? (
          <ul className="flex flex-col gap-0.5">
            {chats.data.map((thread) => {
              const active = pathname === `/chat/${thread.id}`;
              return (
                <li key={thread.id}>
                  <Link
                    to="/chat/$chatId"
                    params={{ chatId: thread.id }}
                    className={cn(
                      "block truncate rounded-[10px] px-3 py-2 text-sm transition-colors",
                      active ? "bg-panel text-paper" : "text-mute hover:bg-panel/60 hover:text-paper",
                    )}
                  >
                    {thread.title || "New chat"}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="px-3 py-2 text-[13px] text-mute">Your conversations will appear here.</p>
        )}
      </div>
      <div className="border-t border-line/60 px-3 py-3">
        <AccountMenu />
      </div>
    </aside>
  );
}

function titleFor(pathname: string): ReactNode {
  if (pathname.startsWith("/chat")) return "Chat";
  if (pathname.startsWith("/library")) return "Library";
  if (pathname.startsWith("/activity")) return "Activity";
  if (pathname.startsWith("/account")) return "Account";
  if (pathname.startsWith("/admin")) return "Admin";
  return "Create";
}

function MobileTopBar({ pathname }: { pathname: string }) {
  return (
    <header className="pt-safe shrink-0 border-b border-line/50 bg-desk/95 backdrop-blur md:hidden">
      <div className="flex h-12 items-center justify-between gap-2 px-4">
        <span className="font-display text-[17px]">{titleFor(pathname)}</span>
        <div className="flex min-w-0 items-center gap-1">
          <AccountMenu className="px-1.5" />
          <GpuStatus />
        </div>
      </div>
    </header>
  );
}

function TabBar({ pathname }: { pathname: string }) {
  return (
    <nav className="pb-safe shrink-0 border-t border-line/60 bg-desk md:hidden" aria-label="Main">
      <div className="grid h-14 grid-cols-4">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              search={navSearch(to)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium",
                active ? "text-paper" : "text-mute",
              )}
            >
              <Icon className={cn("size-[22px]", active && "text-signal")} aria-hidden />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/** Desktop page header: page title on the left, GPU status on the right. */
export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="hidden h-16 shrink-0 items-center gap-4 px-6 md:flex">
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
        {children}
        <GpuStatus />
      </div>
    </div>
  );
}
